import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Table, TableProps, Button, Modal, List, Space, Input } from 'antd';
import { KeyOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { getRequest, postRequest, putRequest, ResponseDataType } from 'api';
import { toast } from 'react-toastify';

interface ParentData {
    parentId: number;
    name: string;
    dob: string;
    email: string;
    phoneNumber: string;
    address: string;
}

interface StudentData {
    studentId: number;
    name: string;
    dob: string;
    gender: number;
    studentCode: string;
    className: string;
}

export const ParentInfo = () => {
    const { parentId } = useParams<{ parentId: string }>();
    const [parent, setParent] = useState<ParentData | null>(null);
    const [students, setStudents] = useState<StudentData[]>([]);
    const [searchResults, setSearchResults] = useState<StudentData[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [loadingSearch, setLoadingSearch] = useState(false);

    const showResetPasswordConfirm = (p: ParentData) => {
        Modal.confirm({
            title: 'Đặt lại mật khẩu cho phụ huynh?',
            icon: <KeyOutlined style={{ color: '#fa8c16' }} />,
            content: `Mật khẩu của phụ huynh "${p.name || ''}" sẽ được đặt lại về chính số điện thoại: ${p.phoneNumber}.`,
            okText: 'Đặt lại mật khẩu',
            okType: 'primary',
            cancelText: 'Hủy',
            async onOk() {
                const url = `/cms/admin/customer/update-parent-password/${p.parentId}?newPassword=${encodeURIComponent(p.phoneNumber)}`;
                const response: ResponseDataType<any> = await putRequest(url);
                if (response?.code === 200) {
                    toast.success(`Đặt lại mật khẩu thành công! Mật khẩu mới là SĐT: ${p.phoneNumber}`);
                } else {
                    toast.error(response?.msg || 'Đặt lại mật khẩu thất bại');
                }
            },
        });
    };

    useEffect(() => {
        if (parentId) {
            fetchParentInfo(parentId);
            fetchStudentList(parentId);
        }
    }, [parentId]);

    const fetchParentInfo = async (id: string) => {
        const response: ResponseDataType<ParentData> = await getRequest(`/cms/parent/${id}`);
        if (response?.code === 200 && response.info) {
            setParent(response.info);
        } else {
            toast.error(response?.msg || 'Không thể tải thông tin phụ huynh');
        }
    };

    const fetchStudentList = async (id: string) => {
        const response: ResponseDataType<StudentData[]> = await getRequest(`/cms/parent/get-student-by-parent?parentId=${id}`);
        if (response?.code === 200 && Array.isArray(response.info)) {
            const updatedStudents = await Promise.all(
                response.info.map(async (student) => {
                    const className = await fetchClassNameByStudent(student.studentId);
                    return { ...student, className };
                })
            );
            setStudents(updatedStudents);
        } else {
            toast.error('Không thể tải danh sách học sinh');
        }
    };

    const fetchClassNameByStudent = async (studentId: number): Promise<string> => {
        const response = await getRequest(`/cms/student/${studentId}/get-classYear-by-student?status=1`);
        if (response?.code === 200 && Array.isArray(response.info)) {
            if (response.info.length > 0 && response.info[0].className) {
                return response.info[0].className;
            } else {
                return 'Chưa tham gia lớp học nào';
            }
        }
        return 'Chưa tham gia lớp học nào';
    };


    const handleSearchByCmnd = async (cmnd: string) => {
        if (!cmnd || cmnd.trim().length < 5) {
            toast.warning('Vui lòng nhập số CCCD hợp lệ');
            return;
        }

        setLoadingSearch(true);
        const response: ResponseDataType<StudentData> = await getRequest(
            `/cms/student/get-student-by-cmnd?cmnd=${encodeURIComponent(cmnd)}`
        );

        if (response?.code === 200 && response.info) {
            const className = await fetchClassNameByStudent(response.info.studentId);
            setSearchResults([{ ...response.info, className }]);
        } else {
            toast.error(response?.msg || 'Không tìm thấy học sinh theo CCCD');
            setSearchResults([]);
        }
        setLoadingSearch(false);
    };


    const handleAddStudent = async (studentId: number) => {
        const response = await postRequest(`/cms/parent/${parentId}/add-student-to-parent`, {
            studentId,
        });

        if (response?.code === 200) {
            toast.success('Đã thêm học sinh');
            fetchStudentList(parentId!);
            setShowModal(false);
        } else {
            toast.error('Thêm học sinh thất bại');
        }
    };

    const columns: TableProps<StudentData>['columns'] = [
        { title: 'ID', dataIndex: 'studentId', key: 'studentId', align: 'center' },
        { title: 'Họ tên', dataIndex: 'name', key: 'name', align: 'center' },
        { title: 'Ngày sinh', dataIndex: 'dob', key: 'dob', align: 'center' },
        {
            title: 'Giới tính',
            dataIndex: 'gender',
            key: 'gender',
            align: 'center',
            render: (gender: number) => (gender === 1 ? 'Nam' : 'Nữ'),
        },
        { title: 'Mã học sinh', dataIndex: 'studentCode', key: 'studentCode', align: 'center' },
        { title: 'Lớp học', dataIndex: 'className', key: 'className', align: 'center' },
    ];

    return (
        <div>

            <Card 
                title="Thông tin phụ huynh"
                extra={
                    parent && (
                        <Button 
                            style={{ color: '#fa8c16', borderColor: '#fa8c16' }}
                            icon={<KeyOutlined />}
                            onClick={() => showResetPasswordConfirm(parent)}
                        >
                            Đặt lại mật khẩu
                        </Button>
                    )
                }
                style={{ marginBottom: 24 }}
            >
                {parent ? (
                    <Row gutter={[16, 16]}>
                        <Col span={12}><strong>Họ tên:</strong> {parent.name}</Col>
                        <Col span={12}><strong>Ngày sinh:</strong> {parent.dob}</Col>
                        <Col span={12}><strong>Email:</strong> {parent.email}</Col>
                        <Col span={12}><strong>SĐT:</strong> {parent.phoneNumber}</Col>
                    </Row>
                ) : (
                    <div>Đang tải thông tin phụ huynh...</div>
                )}
            </Card>

            <h3>Danh sách học sinh</h3>
            <Table
                columns={columns}
                dataSource={students}
                rowKey="studentId"
                pagination={{ pageSize: 5 }}
                locale={{ emptyText: 'Không có học sinh nào' }}
            />

            <Button type="primary" style={{ marginTop: 16 }} onClick={() => setShowModal(true)}>
                Thêm học sinh
            </Button>

            <Modal
                title="Tìm kiếm học sinh bằng CCCD"
                open={showModal}
                onCancel={() => {
                    setShowModal(false);
                    setSearchResults([]);
                }}
                footer={null}
            >
                <Input.Search
                    placeholder="Nhập số CCCD học sinh"
                    enterButton="Tìm kiếm"
                    loading={loadingSearch}
                    onSearch={handleSearchByCmnd}
                />

                <List
                    style={{ marginTop: 16 }}
                    dataSource={searchResults}
                    bordered
                    locale={{ emptyText: 'Không có kết quả' }}
                    renderItem={(item) => (
                        <List.Item
                            actions={[
                                <Button type="link" onClick={() => handleAddStudent(item.studentId)}>Thêm</Button>,
                            ]}
                        >
                            <Space direction="vertical">
                                <div><b>{item.name}</b> ({item.studentCode})</div>
                                <div>{item.dob} - {item.className}</div>
                            </Space>
                        </List.Item>
                    )}
                />
            </Modal>
        </div>
    );
};
